import { type FormEvent, useState } from "react";
import { LegalLayout } from "./legal/LegalLayout";

const CATEGORY_OPTIONS = [
  { value: "サービスに関するご質問", label: "サービスに関するご質問" },
  { value: "料金・プランについて", label: "料金・プランについて" },
  { value: "不具合のご報告", label: "不具合のご報告" },
  { value: "機能リクエスト", label: "機能リクエスト" },
  { value: "その他", label: "その他" },
] as const;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

type FormState = "idle" | "sending" | "success" | "error";

export function SupportPage() {
  const [companyName, setCompanyName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const isValid =
    name.trim() !== "" && email.trim() !== "" && category !== "" && message.trim().length >= 10;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setFormState("sending");
    setErrorMessage("");

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName, name, email, category, message }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ?? "送信に失敗しました");
      }

      setFormState("success");
    } catch (error) {
      setFormState("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "送信に失敗しました。しばらくしてからお試しください。",
      );
    }
  };

  if (formState === "success") {
    return (
      <LegalLayout title="お問い合わせ">
        <div className="rounded-lg border border-green-800 bg-green-900/30 p-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-white">お問い合わせを受け付けました</h2>
          <p className="text-on-surface-variant">
            内容を確認のうえ、担当者よりご連絡いたします。
            <br />
            通常2営業日以内にご返信いたします。
          </p>
        </div>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title="お問い合わせ">
      <p className="text-on-surface-variant">
        スカウトメールAIに関するお問い合わせは、下記フォームよりお送りください。
      </p>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div>
          <label className="mb-1 block text-sm font-medium text-white" htmlFor="name">
            お名前 <span className="text-red-400">*</span>
          </label>
          <input
            className="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
            id="name"
            onChange={(e) => setName(e.target.value)}
            placeholder="例: 山田 太郎"
            required
            type="text"
            value={name}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-white" htmlFor="email">
            メールアドレス <span className="text-red-400">*</span>
          </label>
          <input
            className="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
            id="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="例: info@example.com"
            required
            type="email"
            value={email}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-white" htmlFor="companyName">
            会社名
          </label>
          <input
            className="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
            id="companyName"
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="例: 株式会社○○"
            type="text"
            value={companyName}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-white" htmlFor="category">
            お問い合わせ種別 <span className="text-red-400">*</span>
          </label>
          <select
            className="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
            id="category"
            onChange={(e) => setCategory(e.target.value)}
            required
            value={category}
          >
            <option value="">選択してください</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-white" htmlFor="message">
            お問い合わせ内容 <span className="text-red-400">*</span>
          </label>
          <textarea
            className="w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
            id="message"
            onChange={(e) => setMessage(e.target.value)}
            placeholder="お問い合わせ内容をご記入ください（10文字以上）"
            required
            rows={6}
            value={message}
          />
          {message.length > 0 && message.length < 10 && (
            <p className="mt-1 text-sm text-red-400">10文字以上で入力してください</p>
          )}
        </div>

        {formState === "error" && (
          <div className="rounded-md border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-400">
            {errorMessage}
          </div>
        )}

        <button
          className="rounded-full bg-white px-8 py-3 font-bold text-on-primary transition-transform hover:scale-[1.02] disabled:opacity-50"
          disabled={!isValid || formState === "sending"}
          type="submit"
        >
          {formState === "sending" ? "送信中..." : "送信する"}
        </button>
      </form>
    </LegalLayout>
  );
}
